import { CANVAS_AD_ROW_GAP, CANVAS_AD_METADATA_HEIGHT, canvasCellSize } from '@shared/canvasGeometry';
/**
 * Canvas grid layout — the SINGLE source of truth for where ad tiles sit.
 *
 * Pure + dependency-free on purpose: the store imports it for live de-stacking
 * AND a node test imports it directly, so the algorithm that ships is the exact
 * algorithm that's verified.
 *
 * Grid constants match the SERVER grid (playbookExecutor.ts): start at (60,60),
 * 5 columns with a 40px horizontal gap and 80px vertical gap. Cell dimensions
 * grow to the largest rendered tile so QA/title annotations clear the next row.
 */
export const GRID = { START_X: 60, START_Y: 60, COLS: 5, STEP_X: 340, STEP_Y: 300 + CANVAS_AD_ROW_GAP } as const;

export interface LayoutItem {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** True if a and b overlap by more than `tol` px on BOTH axes. */
function overlaps(a: LayoutItem, b: LayoutItem, tol = 5): boolean {
  return (
    Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x) > tol &&
    Math.min(a.y + a.h + CANVAS_AD_METADATA_HEIGHT, b.y + b.h + CANVAS_AD_METADATA_HEIGHT) - Math.max(a.y, b.y) > tol
  );
}

/** True if ANY two items in the set overlap. */
export function hasOverlaps(items: LayoutItem[]): boolean {
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      if (overlaps(items[i], items[j])) return true;
    }
  }
  return false;
}

/**
 * Compute clean grid positions for a set of tiles.
 *
 * Returns a Map of key -> {x,y} containing ONLY the tiles that need to move.
 * When the set is already overlap-free, returns an EMPTY map — callers treat
 * that as "leave everything alone", so a clean / manually-arranged canvas is
 * never disturbed and there's no render thrash.
 *
 * When there IS an overlap, every tile is re-laid into a clean ordered 5-col
 * grid, sorted by current reading order (top-to-bottom, then left-to-right,
 * with key as a deterministic tiebreaker so two tiles sharing a slot resolve
 * the same way every time). Only tiles whose computed slot differs from their
 * current position appear in the result map.
 */
export function computeGridLayout(
  items: LayoutItem[],
  opts?: { force?: boolean },
): Map<string, { x: number; y: number }> {
  const result = new Map<string, { x: number; y: number }>();
  if (items.length === 0) return result;
  // Normal mode: only re-grid when something overlaps (preserves a clean or
  // manually-arranged canvas). Force mode (used once at batch completion):
  // ALWAYS pack into a contiguous, gap-free grid — this is what removes empty
  // spots left by dropped tiles or cleared loading boxes.
  if (!opts?.force) {
    if (items.length < 2) return result;
    if (!hasOverlaps(items)) return result; // already clean — preserve arrangement
  }

  const sorted = [...items].sort(
    (a, b) => a.y - b.y || a.x - b.x || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0),
  );
  const cell = canvasCellSize(items);
  sorted.forEach((it, idx) => {
    const x = GRID.START_X + (idx % GRID.COLS) * (cell.width + 40);
    const y = GRID.START_Y + Math.floor(idx / GRID.COLS) * (cell.height + CANVAS_AD_ROW_GAP);
    if (Math.round(it.x) !== x || Math.round(it.y) !== y) result.set(it.key, { x, y });
  });
  return result;
}
